import { Bookmark, Clipboard } from "lucide-react";

/**
 * Bookmark Tool for Editor.js - creates standalone bookmark blocks
 */
export default class BookmarkTool {
  /**
   * Get Tool toolbox settings
   */
  static get toolbox() {
    return {
      title: "Закладка",
      icon: '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>',
    };
  }

  /**
   * Sanitize rules
   */
  static get sanitize() {
    return {
      id: true,
      name: true,
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.readOnly = readOnly;
    this.data = {
      id: data?.id || this.generateId(),
      name: data?.name || "Закладка",
    };
    this.config = config || {};
  }

  /**
   * Generate unique ID for bookmark
   */
  generateId() {
    return Math.random().toString(36).substr(2, 9);
  }

  /**
   * Render bookmark block
   */
  render() {
    const wrapper = document.createElement("div");
    wrapper.className = "bookmark-block";

    // Определяем тип отображения
    const isViewArticle = this.config.viewMode === "viewArticle";
    const isArticleForUser = this.config.viewMode === "articleForUser";

    if (this.readOnly && isArticleForUser) {
      // Режим чтения для ArticleForUser - компактный якорь
      wrapper.className = "bookmark-block-readonly";
      wrapper.style.cssText = `
        position: relative;
        height: 20px;
        width: 100%;
        margin: 0;
        padding: 0;
        border: none;
        background: transparent;
      `;

      // Основной якорь для навигации
      const anchor = document.createElement("div");
      anchor.id = this.data.id;
      anchor.className = "bookmark-anchor-visible";
      anchor.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        height: 20px;
        width: 100%;
        visibility: visible;
        pointer-events: none;
        background: transparent;
      `;
      anchor.setAttribute("data-bookmark-id", this.data.id);
      anchor.setAttribute("data-bookmark-name", this.data.name);

      wrapper.appendChild(anchor);
    } else if (this.readOnly && isViewArticle) {
      // Режим чтения для ViewArticle - полное отображение, но только для копирования
      wrapper.className = "bookmark-block-admin";
      wrapper.style.cssText = `
        background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
        border: 2px solid #388ae5;
        border-radius: 8px;
        padding: 15px;
        margin: 10px 0;
        position: relative;
        box-shadow: 0 2px 8px rgba(56, 138, 229, 0.1);
      `;

      const header = document.createElement("div");
      header.style.cssText = `
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
      `;

      // Bookmark name (только для отображения)
      const nameElement = document.createElement("div");
      nameElement.style.cssText = `
        font-weight: 600;
        color: #495057;
        font-size: 16px;
        min-width: 100px;
        padding: 2px 4px;
        border-radius: 3px;
      `;
      nameElement.textContent = this.data.name;

      // Bookmark ID
      const idElement = document.createElement("div");
      idElement.style.cssText = `
        font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
        font-size: 12px;
        color: #6c757d;
        background: #e9ecef;
        padding: 4px 8px;
        border-radius: 4px;
        flex-grow: 1;
      `;
      idElement.textContent = `ID: ${this.data.id}`;

      // Copy button для ViewArticle
      const copyButton = document.createElement("button");
      copyButton.style.cssText = `
        background: #388ae5;
        border: 1px solid #388ae5;
        border-radius: 4px;
        padding: 6px;
        cursor: pointer;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
      `;
      copyButton.title = "Скопировать ссылку на закладку";

      const clipboardIcon = this.createClipboardIcon();
      copyButton.appendChild(clipboardIcon);

      // Обработчик копирования для ViewArticle
      copyButton.addEventListener("click", () => {
        // Генерируем ссылку с закладкой
        const bookmarkLink = this.generateBookmarkLink();
        navigator.clipboard.writeText(bookmarkLink).then(() => {
          const checkIcon = this.createCheckIcon();
          copyButton.innerHTML = "";
          copyButton.appendChild(checkIcon);
          copyButton.title = "Ссылка скопирована!";

          setTimeout(() => {
            copyButton.innerHTML = "";
            copyButton.appendChild(clipboardIcon);
            copyButton.title = "Скопировать ссылку на закладку";
          }, 2000);
        });
      });

      header.appendChild(nameElement);
      header.appendChild(idElement);
      header.appendChild(copyButton);
      wrapper.appendChild(header);

      // Добавляем якорь
      const anchor = document.createElement("div");
      anchor.id = this.data.id;
      anchor.className = "bookmark-anchor";
      anchor.style.cssText = `
        position: absolute;
        top: -120px;
        display: block;
        height: 0;
        width: 0;
      `;
      wrapper.appendChild(anchor);
    } else {
      // Режим редактирования - полное отображение с редактированием
      wrapper.className = "bookmark-block-admin";
      wrapper.style.cssText = `
        background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
        border: 2px solid #388ae5;
        border-radius: 8px;
        padding: 15px;
        margin: 10px 0;
        position: relative;
        box-shadow: 0 2px 8px rgba(56, 138, 229, 0.1);
      `;

      const header = document.createElement("div");
      header.style.cssText = `
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
      `;

      // Bookmark name (редактируемый)
      const nameElement = document.createElement("div");
      nameElement.style.cssText = `
        font-weight: 600;
        color: #495057;
        font-size: 16px;
        min-width: 100px;
        border: 1px dashed transparent;
        padding: 2px 4px;
        border-radius: 3px;
      `;
      nameElement.textContent = this.data.name;
      nameElement.contentEditable = "true";

      // Bookmark ID
      const idElement = document.createElement("div");
      idElement.style.cssText = `
        font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
        font-size: 12px;
        color: #6c757d;
        background: #e9ecef;
        padding: 4px 8px;
        border-radius: 4px;
        flex-grow: 1;
      `;
      idElement.textContent = `ID: ${this.data.id}`;

      // Copy button для редактирования - теперь копирует полную ссылку
      const copyButton = document.createElement("button");
      copyButton.style.cssText = `
        background: #388ae5;
        border: 1px solid #388ae5;
        border-radius: 4px;
        padding: 6px;
        cursor: pointer;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
      `;
      copyButton.title = "Скопировать ссылку на закладку"; // Изменен текст подсказки

      const clipboardIcon = this.createClipboardIcon();
      copyButton.appendChild(clipboardIcon);

      // Обработчик копирования для режима редактирования - теперь копирует полную ссылку
      copyButton.addEventListener("click", () => {
        // Генерируем полную ссылку с закладкой
        const bookmarkLink = this.generateBookmarkLink();
        navigator.clipboard.writeText(bookmarkLink).then(() => {
          const checkIcon = this.createCheckIcon();
          copyButton.innerHTML = "";
          copyButton.appendChild(checkIcon);
          copyButton.title = "Ссылка скопирована!";

          setTimeout(() => {
            copyButton.innerHTML = "";
            copyButton.appendChild(clipboardIcon);
            copyButton.title = "Скопировать ссылку на закладку";
          }, 2000);
        });
      });

      header.appendChild(nameElement);
      header.appendChild(idElement);
      header.appendChild(copyButton);
      wrapper.appendChild(header);

      // Add anchor для редактирования
      const anchor = document.createElement("div");
      anchor.id = this.data.id;
      anchor.className = "bookmark-anchor";
      anchor.style.cssText = `
        position: absolute;
        top: -120px;
        display: block;
        height: 0;
        width: 0;
      `;
      wrapper.appendChild(anchor);

      // Handle name changes
      nameElement.addEventListener("blur", () => {
        this.data.name = nameElement.textContent || "Закладка";
      });

      nameElement.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          nameElement.blur();
        }
      });
    }

    return wrapper;
  }

  /**
   * Generate bookmark link for ViewArticle mode
   */
  generateBookmarkLink() {
    // Используем serverPath из конфигурации или вычисляем базовый URL
    const serverPath = this.config.serverPath || window.location.origin;
    const articleHash = this.config.articleHash || this.extractArticleHashFromUrl();

    return `${serverPath}/art/${articleHash}?bookmark=${this.data.id}`;
  }

  /**
   * Extract article hash from current URL
   */
  extractArticleHashFromUrl() {
    const currentUrl = window.location.href;
    const urlParts = currentUrl.split("/");
    return urlParts[urlParts.length - 1].split("?")[0];
  }

  /**
   * Create SVG icon for clipboard
   */
  createClipboardIcon() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", "16");
    svg.setAttribute("height", "16");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");

    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", "8");
    rect.setAttribute("y", "8");
    rect.setAttribute("width", "8");
    rect.setAttribute("height", "8");
    rect.setAttribute("rx", "1");
    rect.setAttribute("ry", "1");

    const path1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path1.setAttribute("d", "M16 4h2a2 2 0 0 1 2 2v2");

    const path2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path2.setAttribute("d", "M8 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2");

    svg.appendChild(rect);
    svg.appendChild(path1);
    svg.appendChild(path2);

    return svg;
  }

  /**
   * Create SVG check icon
   */
  createCheckIcon() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", "16");
    svg.setAttribute("height", "16");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M20 6L9 17l-5-5");

    svg.appendChild(path);
    return svg;
  }

  /**
   * Save bookmark data
   */
  save() {
    return {
      id: this.data.id,
      name: this.data.name,
    };
  }

  /**
   * Validate bookmark data
   */
  validate(savedData) {
    return !!(savedData.id && savedData.id.trim());
  }
}
